import { memo } from 'react';
import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import LockIcon from '@mui/icons-material/Lock';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ViewComfyOutlinedIcon from '@mui/icons-material/ViewComfyOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import BlockIcon from '@mui/icons-material/Block';
import { bloxValues } from 'themes/constants';
import getStatusColor from 'components/Search/utility/getStatusColor';
import { toBoolean } from 'utility/functions';

import type { SvgIconComponent } from '@mui/icons-material';
import type {
  StatusIndicatorKey,
  HasStatus,
} from 'components/Search/components/fields/generic/StatusIndicators/types/status';

type IndicatorConfig<TRecord extends Record<string, unknown>> = {
  key: keyof TRecord & StatusIndicatorKey;
  Icon: SvgIconComponent;
  labelKey: string;
  color?:
    | string
    | ((args: { record: TRecord; theme: Theme; key: StatusIndicatorKey }) => string);
  isActive?: (record: TRecord) => boolean;
  ariaLabel?: string;
};

export type StatusIndicatorsProps<
  TRecord extends HasStatus & Partial<Record<StatusIndicatorKey, unknown>> = HasStatus &
    Partial<Record<StatusIndicatorKey, unknown>>,
> = {
  record: TRecord | null | undefined;
  statuses?: IndicatorConfig<TRecord>[];
  iconFontSize?: number | string;
  iconGap?: number;
  containerSx?: SxProps<Theme>;
};

const defaultIndicatorConfigs = [
  {
    key: 'locked',
    labelKey: 'blox.editorial.locked.label',
    Icon: LockIcon,
  },
  {
    key: 'blocked_by_workflow',
    labelKey: 'blox.editorial.metadata.group.workflow.help.restricted',
    Icon: ErrorOutlineIcon,
  },
  {
    key: 'no_publish',
    labelKey: 'blox.editorial.asset.properties.no_publish.help.disallow',
    Icon: BlockIcon,
    isActive: (record: any) => record?.no_publish === 1 || record?.no_publish === '1',
  },
  {
    key: 'draft',
    labelKey: 'blox.editorial.asset.draft.description',
    Icon: EditNoteIcon,
  },
  {
    key: 'placed_on_page',
    labelKey: 'blox.general.placed_on_page',
    Icon: ViewComfyOutlinedIcon,
  },
];

function StatusIndicatorsDisplay<
  TRecord extends HasStatus & Partial<Record<StatusIndicatorKey, unknown>>,
>({
  record,
  statuses,
  iconFontSize = bloxValues.regularFontSizeRem,
  iconGap = 0.75,
  containerSx,
}: StatusIndicatorsProps<TRecord>) {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!record) return null;

  const indicatorConfigs =
    (statuses as IndicatorConfig<TRecord>[] | undefined) ??
    (defaultIndicatorConfigs as IndicatorConfig<TRecord>[]);

  const activeIndicators = indicatorConfigs.filter((config) =>
    config.isActive ? config.isActive(record) : toBoolean((record as any)[config.key]),
  );

  if (activeIndicators.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: iconGap,
        ...containerSx,
      }}
      data-testid="status-indicators"
    >
      {activeIndicators.map(({ key, labelKey, Icon, color, ariaLabel }) => {
        const label = t(labelKey);
        const iconColor =
          typeof color === 'function'
            ? color({ record, theme, key })
            : color ?? getStatusColor(record, theme, key);

        return (
          <Tooltip
            key={key}
            title={label}
            placement="top"
          >
            <span aria-label={ariaLabel ?? label}>
              <Icon sx={{ fontSize: iconFontSize, color: iconColor }} />
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
}

const StatusIndicators = memo(StatusIndicatorsDisplay);
export default StatusIndicators;
